import React, { useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { Mail, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react'
import { Turnstile } from '@marsidev/react-turnstile'
import { trackPasswordResetRequest } from '../../config/analyticsEvents'
import textBanner from '../../assets/images/text-banner-black-min.png'

const ForgotPasswordForm = ({ onToggleForm }) => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const turnstileRef = useRef()
  const navigate = useNavigate()

  const { resetPassword } = useAuth()

  // Check if this is a standalone page or embedded in Auth
  const isStandalonePage = !onToggleForm

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const { error } = await resetPassword(email, captchaToken)
    
    if (error) {
      setError(error.message)
      if (turnstileRef.current) {
        turnstileRef.current.reset()
        setCaptchaToken('')
      }
    } else {
      trackPasswordResetRequest()
      setSuccess(true)
    }
    
    setLoading(false)
  }

  const handleBackToSignIn = () => {
    if (onToggleForm) {
      onToggleForm('login')
    } else {
      navigate('/auth')
    }
  }

  // Standalone page wrapper
  const PageWrapper = ({ children }) => {
    if (!isStandalonePage) {
      return children
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex flex-col">
        <div className="flex-1 p-8">
          <div className="w-full max-w-md mx-auto min-h-full flex flex-col justify-center">
            {/* Brand Banner */}
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center hover:opacity-80 transition-opacity">
                <div className="relative">
                  <img 
                    src={textBanner} 
                    alt="EasyAppIcon API" 
                    className="h-10"
                  />
                  <span className="absolute -top-1 -right-8 bg-[#df6e57] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    API
                  </span>
                </div>
              </Link>
            </div>
            {children}
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <PageWrapper>
        <div className="w-full max-w-md mx-auto">
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-green-400/30 via-emerald-400/30 to-teal-400/30 rounded-3xl blur-2xl -z-10" />
            <div className="glass-effect bg-white rounded-2xl shadow-soft hover:shadow-hover transition-shadow p-6 sm:p-8 text-center">
              <div className="mb-6">
                <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-700 mb-4">Check your email</h2>
              <p className="text-gray-600 mb-8">
                We've sent a password reset link to{' '}
                <span className="font-medium">{email}</span>
              </p>
              <button
                onClick={handleBackToSignIn}
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors hover:underline"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <div className="w-full max-w-md mx-auto">
        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-orange-400/30 via-red-400/30 to-pink-400/30 rounded-3xl blur-2xl -z-10" />
          <div className="glass-effect bg-white rounded-2xl shadow-soft hover:shadow-hover transition-shadow p-6 sm:p-8">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-700 mb-2">Reset password</h2>
              <p className="text-gray-600">
                Enter your email and we'll send you a reset link
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" aria-live="polite">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-3">
                  Email address
                </label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5 group-focus-within:text-orange-500 transition-colors" />
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 w-full px-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200 bg-gray-50/50 hover:bg-white focus:bg-white shadow-sm"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              {/* Cloudflare Turnstile CAPTCHA */}
              <div className="w-full overflow-hidden pr-[4px] py-1">
                <div className="flex justify-center">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                    onSuccess={(token) => setCaptchaToken(token)}
                    onError={() => {
                      setCaptchaToken('')
                      setError('CAPTCHA verification failed. Please try again.')
                    }}
                    onExpire={() => {
                      setCaptchaToken('')
                      setError('CAPTCHA expired. Please verify again.')
                    }}
                    options={{
                      theme: 'light',
                      size: 'flexible'
                    }}
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !captchaToken}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-4 rounded-xl font-semibold focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Sending...</span>
                  </div>
                ) : (
                  'Send reset link'
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToSignIn}
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors hover:underline"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to sign in
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </PageWrapper>
  )
}

export default ForgotPasswordForm
